/**
 * Disposal Portal API
 * Connects to Azure Functions backend -> Cosmos DB (DisposalWorkflow + Tools join)
 */

import { offlineEngine } from '../../../shared/services/offlineEngine';

import { apiFetch } from '../../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';
// ── Transform ────────────────────────────────────────────────────────────────

function transformDisposal(d) {
  return {
    id:                   d.disposal_id,
    disposalId:           d.disposal_id,
    toolId:               d.supplier_tool_id || d.tool_id,
    internalToolId:       d.tool_id,
    internalDisposalId:   d.disposal_id,
    oem:                  d.oem,
    toolType:             d.tool_type,
    altToolId:            d.alt_tool_id,
    weight:               d.estimated_weight,
    weightUnit:           d.weight_unit || 'LBS',
    size:                 d.size_dimensions,
    partDescription:      d.part_description,
    siteLoc:              d.site_location,
    sectionDetail:        d.detail_location,
    rowNbr:               d.row_nbr,
    stackNbr:             d.stack_nbr,
    rackNbr:              d.rack_nbr,
    shelfNbr:             d.shelf_nbr,
    skidNbr:              d.skid_nbr,
    supplierName:         d.site_name || d.site_id,
    siteId:               d.site_id,
    siteCity:             d.site_city,
    siteState:            d.site_state_province,
    scrapProjectNumber:   d.scrap_project_number,
    marked:               d.marked,
    staged:               d.staged,
    scrapStatus:          d.scrap_status,
    shipDate:             d.ship_date,
    truckNbr:             d.truck_nbr,
    dayTripNbr:           d.day_trip_nbr,
    bolNbr:               d.bol_nbr,
    disposalComments:     d.disposal_comments,
    approvedScrap:        d.approved_scrap,
    oemScrapRights:       d.oem_scrap_rights,
    photos:               [],
    loadMedia:            [],
    bolMedia:             [],
    createdAt:            d.created_at,
    updatedAt:            d.updated_at,
  };
}

// ── Reads ────────────────────────────────────────────────────────────────────

export async function getDisposalTools(filters = {}, callerEmail = null) {
  const params = new URLSearchParams();
  if (callerEmail)                 params.append('caller_email', callerEmail);
  if (filters.marked)              params.append('marked', filters.marked);
  if (filters.scrapStatus)         params.append('scrap_status', filters.scrapStatus);
  if (filters.scrapProjectNumber)  params.append('scrap_project_number', filters.scrapProjectNumber);
  if (filters.search)              params.append('search', filters.search);

  const url = `${API_BASE_URL}/get_disposal${params.toString() ? '?' + params.toString() : ''}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  const items = await response.json();
  return items.map(transformDisposal);
}

export async function getDisposalTool(disposalId, callerEmail = null) {
  const params = new URLSearchParams({ disposal_id: disposalId });
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/get_disposal?${params}`);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  const items = await response.json();
  if (!items.length) throw new Error('Disposal record not found');
  return transformDisposal(items[0]);
}

export async function getFilterOptions(callerEmail = null) {
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/get_disposal${params.toString() ? '?' + params.toString() : ''}`);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  const items = await response.json();
  return {
    projects:       [...new Set(items.map(d => d.scrap_project_number))].filter(Boolean).sort(),
    markedStatuses: [...new Set(items.map(d => d.marked))].filter(Boolean).sort(),
    scrapStatuses:  [...new Set(items.map(d => d.scrap_status))].filter(Boolean).sort(),
  };
}

// ── Site-scoped reads (S6 site-picker path) ─────────────────────────────────
// These hit the same backend endpoints S1 Asset Registry uses (get_sites /
// get_tools / search_inventory) but are owned by the disposal-portal feature
// rather than imported cross-feature. No backend change. Air-gap is enforced
// server-side via caller_email on all three.

/**
 * Sites available to the caller.
 * GET /get_sites?caller_email={email}
 * Returns raw site docs (array, or {sites:[]}); callers normalize.
 */
export async function getDisposalPortalSites(callerEmail = null) {
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);
  const response = await apiFetch(`${API_BASE_URL}/get_sites?${params}`);
  if (!response.ok) throw new Error(`get_sites error: ${response.status}`);
  return response.json();
}

/**
 * All tools at a site — raw snake_case Tools docs. This is the "show all"
 * payload for the site-scoped view when there is no active search term
 * (search_inventory returns [] below MIN_QUERY_LENGTH, so it cannot serve
 * the no-search case).
 * GET /get_tools?site_id={siteId}&caller_email={email}
 */
export async function getSiteTools(siteId, callerEmail = null) {
  const params = new URLSearchParams({ site_id: siteId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const response = await apiFetch(`${API_BASE_URL}/get_tools?${params}`);
  if (!response.ok) throw new Error(`get_tools error: ${response.status}`);
  return response.json();
}

/**
 * Canonical multi-container search (Tools + AssetTags + PartNumbers) scoped
 * to a single site. Raw snake_case tool docs; asset-tag / part-number hits
 * carry a `_match_context` annotation. Backend returns [] for queries shorter
 * than MIN_QUERY_LENGTH.
 * GET /search_inventory?site_id={siteId}&q={query}&caller_email={email}
 */
export async function searchSiteInventory(siteId, query, callerEmail = null) {
  const params = new URLSearchParams({ site_id: siteId, q: query });
  if (callerEmail) params.append('caller_email', callerEmail);
  const response = await apiFetch(`${API_BASE_URL}/search_inventory?${params}`);
  if (!response.ok) throw new Error(`search_inventory error: ${response.status}`);
  return response.json();
}

export async function loadToolMedia(toolId, callerEmail = null) {
  return offlineEngine.getMedia(toolId, callerEmail);
}

// ── Writes ────────────────────────────────────────────────────────────────────

export async function updateDisposalTool(disposalId, internalToolId, updates, callerEmail = null) {
  const result = await offlineEngine.enqueueTextUpdate({
    toolId:       internalToolId,
    disposalId:   disposalId,
    target:       'disposal',
    fieldChanges: updates,
    callerEmail,
  });

  if (result.queued) {
    return { queued: true, outbox_id: result.outbox_id };
  }

  return getDisposalTool(disposalId, callerEmail);
}

export async function uploadDisposalPhoto({ toolId, disposalId, photoType, file, uploadedBy, siteId, callerEmail = null }) {
  return offlineEngine.enqueuePhoto({
    toolId,
    disposalId,
    photoType,
    file,
    uploadedBy,
    siteId,
    callerEmail,
  });
}

// ── Delete ────────────────────────────────────────────────────────────────────

// ---------------------------------------------------------------------------
// deleteDisposalPhoto
// ---------------------------------------------------------------------------
// Hard-deletes the blob from Blob Storage, soft-deletes the Cosmos Media doc.
// ADMIN can delete any photo. All other roles can only delete their own uploads.
//
// @param {string} mediaId      — Media document ID (e.g. "media-tool-autokiniton-002-abc12345")
// @param {string} callerEmail  — MSAL UPN of the logged-in user
// ---------------------------------------------------------------------------
export async function deleteDisposalPhoto(mediaId, callerEmail = null) {
  const params = new URLSearchParams({ media_id: mediaId });
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/delete_media?${params.toString()}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `delete_media failed with status ${response.status}`);
  }

  return await response.json();
}
